import { cast, types } from 'mobx-state-tree';

export enum ModalType {
  DeveloperMenu = 'DeveloperMenu',
  DetailPane = 'DetailPane',
  Search = 'Search',
  Settings = 'Settings',
  Login = 'Login',
}

export enum SearchableEntity {
  Commodity = 'Commodity',
  Contract = 'Contract',
}

export interface OpenSearchProps {
  searchableEntities?: SearchableEntity[];
  initialQuery?: string;
  blockId?: string;
}
type SelectedSearchItem = {
  entity: SearchableEntity;
  id: string;
  label?: string; // freeform/catalog name carried through when the id isn't a market commodity
};
interface SearchNavigationState {
  blockId?: string;
  selectedItems?: SelectedSearchItem[];
}
const modalTypes = Object.values(ModalType);

export const PageStore = types
  .model('PageStore', {
    currentModal: types.maybe(types.enumeration(modalTypes)),
    selectedSettingPath: types.maybe(types.string),
    sidebarPath: types.maybe(types.string), // kept in sync via usePagePathSyncer
    loginRedirectUrl: types.maybe(types.string),
    searchState: types.maybe(
      types.model('SearchState', {
        searchableEntities: types.array(types.enumeration(Object.values(SearchableEntity))),
        initialQuery: types.maybe(types.string),
        initialBlockId: types.maybe(types.string),
      }),
    ),
    searchNavigationState: types.maybe(
      types.model('SearchNavigationState', {
        blockId: types.maybe(types.string),
        selectedItems: types.array(
          types.model('SelectedItem', {
            entity: types.enumeration(Object.values(SearchableEntity)),
            id: types.string,
            label: types.maybe(types.string),
          }),
        ),
      }),
    ),
  })
  .actions((self) => ({
    openModal(modalType: ModalType) {
      self.currentModal = modalType;
    },
    closeModal() {
      self.currentModal = undefined;
      self.searchState = undefined;
      self.loginRedirectUrl = undefined;
    },
    setSelectedSettingPath(setting: string | undefined) {
      self.selectedSettingPath = setting;
    },
    setSidebarPath(path: string | undefined) {
      self.sidebarPath = path;
    },
    openSearchModal({ searchableEntities, initialQuery, blockId }: OpenSearchProps = {}) {
      self.currentModal = ModalType.Search;
      if (searchableEntities != null) {
        self.searchState = {
          searchableEntities: cast(searchableEntities),
          initialQuery,
          initialBlockId: blockId,
        };
      }
    },
    setSearchNavigationState({ blockId, selectedItems }: SearchNavigationState) {
      self.searchNavigationState = {
        blockId: blockId ?? self.searchState?.initialBlockId,
        selectedItems: cast(selectedItems),
      };
    },
    setLoginRedirectUrl(url: string | undefined) {
      self.loginRedirectUrl = url;
    },
    clearLoginRedirectUrl() {
      self.loginRedirectUrl = undefined;
    },
    clearSearchNavigationState() {
      self.searchNavigationState = undefined;
    },
  }))
  .views((self) => ({
    isSettingsModalOpen: () => self.currentModal === ModalType.Settings,
    isLoginModalOpen: () => self.currentModal === ModalType.Login,
    isSearchModalOpen: () => self.currentModal === ModalType.Search,
    initialSearchQuery: () => self.searchState?.initialQuery,
    searchableEntities: () => self.searchState?.searchableEntities,
    selectedSearchItems: () => self.searchNavigationState?.selectedItems,
    selectedSearchBlockId: () => self.searchNavigationState?.blockId,
  }));

export const createPageStore = () => {
  return PageStore.create({});
};
